import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { useMasterData } from '../context/MasterDataContext';
import { LinearProgress, Box } from '@mui/material';
import { Platforms } from './Platforms';
import { MD } from '../masterData.classes';

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
    className?: string;
}

const TabPanel = ({ children, value, index, className, ...other }: TabPanelProps): React.JSX.Element | null => {
    if (value !== index) return null;
    return (
        <Paper
            component="div"
            className={className}
            role="tabpanel"
            id={`master-tabpanel-${index}`}
            aria-labelledby={`master-tab-${index}`}
            sx={{
                p: '24px',
                display: 'flex',
                flex: 1,
                backgroundColor: 'grey.50',
            }}
            {...other}
        >
            {children}
        </Paper>
    );
};

interface TabConfig {
    label: string;
    Component: React.ComponentType;
    tabClassName: string;
    tabPanelClassName: string;
}

export const MasterDataLayout = (): React.JSX.Element => {
    const [tabValue, setTabValue] = useState<number>(0);

    const { t } = useTranslation();
    const { isLoading } = useMasterData();

    const handleChange = (_: any, newValue: number): void => {
        setTabValue(newValue);
    };

    const tabsConfig: TabConfig[] = [
        {
            label: t('masterData.platforms'),
            Component: Platforms,
            tabClassName: MD.tabPlatforms,
            tabPanelClassName: MD.tabPanelPlatforms,
        },
    ];

    return (
        <SCUIContent>
            <Box className={MD.page}>
                <Paper
                    className={MD.shellPaper}
                    component="div"
                    sx={{
                        p: '24px',
                        paddingTop: '36px',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    <div className={MD.loadingSection}>{isLoading && <LinearProgress />}</div>
                    <div className={MD.breadcrumbSection}>
                        <SCUIBreadCrumb title={t('masterData.title')} componentLink={'/master-data'} />
                    </div>
                    <Box className={MD.tabsWrap}>
                        <Tabs
                            className={MD.tabsRoot}
                            sx={{ marginTop: '12px' }}
                            value={tabValue}
                            onChange={handleChange}
                            aria-label="Master Data Tabs"
                        >
                            {tabsConfig.map((tab, index) => (
                                <Tab
                                    key={index}
                                    className={tab.tabClassName}
                                    sx={{ fontSize: '14px' }}
                                    label={tab.label}
                                    id={`master-tab-${index}`}
                                    aria-controls={`master-tabpanel-${index}`}
                                />
                            ))}
                        </Tabs>
                    </Box>
                    {tabsConfig.map((tab, index) => (
                        <TabPanel key={index} value={tabValue} index={index} className={tab.tabPanelClassName}>
                            <tab.Component />
                        </TabPanel>
                    ))}
                </Paper>
            </Box>
        </SCUIContent>
    );
};
